'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useContext, useEffect, useRef } from 'react';
import { useVideoArming } from '@/lib/hooks/useVideoArming';
import { usePathname } from 'next/navigation';
import TransitionContext from '@/components/Mobile/Transition/TransitionContext';
import { useCollapseNavigate } from '@/lib/hooks/useCollapseNavigate';
import type { BlogPostModel } from '@/lib/models/BlogPost';
import {
  BLOG_THUMBNAIL_WIDTH,
  BLOG_THUMBNAIL_HEIGHT,
} from '@/lib/constants/media';
import styles from './blog-mobile.module.scss';

const COLLAPSE_DURATION_MS = 300;

interface Props {
  posts: BlogPostModel[];
}

interface CardProps {
  post: BlogPostModel;
  eager?: boolean;
  isCollapsing: boolean;
  onNavigate: (href: string) => void;
}

function BlogCardMobile({ post, eager = false, isCollapsing, onNavigate }: CardProps) {
  const isVideo = post.thumbnail?.endsWith('.mp4') ?? false;
  const videoRef = useRef<HTMLVideoElement>(null);
  useVideoArming({ videoRef, initialLoad: isVideo, initialPlay: isVideo });

  return (
    <div className={isCollapsing ? styles.collapseWindow : styles.expandWindow}>
    <article className={styles.blogCardMobile}>
      <div className={styles.blogCardMobileTitleStrip}>
        <img
          src='/assets/image/mobile/LAB_NOTES.svg'
          alt=''
          aria-hidden='true'
        />
        <h2 className={styles.blogCardMobileTitle}>{post.title.toUpperCase()}</h2>
      </div>
      <div
        className={styles.blogCardMobileMedia}
        style={{ aspectRatio: `${BLOG_THUMBNAIL_WIDTH} / ${BLOG_THUMBNAIL_HEIGHT}` }}
      >
        {post.thumbnail ? (
          isVideo ? (
            <video
              ref={videoRef}
              src={post.thumbnail}
              preload='metadata'
              muted
              playsInline
              loop
              autoPlay
              aria-label={`${post.title} video`}
              style={{ aspectRatio: `${BLOG_THUMBNAIL_WIDTH} / ${BLOG_THUMBNAIL_HEIGHT}` }}
            >
              <track
                kind='captions'
                src='/captions/no-audio.vtt'
                srcLang='en'
                label='No spoken audio'
              />
            </video>
          ) : (
            <Image
              src={post.thumbnail}
              alt={post.title}
              width={BLOG_THUMBNAIL_WIDTH}
              height={BLOG_THUMBNAIL_HEIGHT}
              sizes='100vw'
              unoptimized={post.thumbnail.endsWith('.gif')}
              loading={eager ? 'eager' : undefined}
            />
          )
        ) : null}
      </div>
      <div className={styles.blogCardMobileBottom}>
        <p className={styles.blogCardMobileExcerpt}>{post.excerpt}</p>
        <Link
          href={`/blog/${post.slug}`}
          className={styles.blogCardMobileReadMore}
          onClick={(event) => {
            event.preventDefault();
            onNavigate(`/blog/${post.slug}`);
          }}
        >
          READ MORE
        </Link>
      </div>
    </article>
    </div>
  );
}

export default function BlogIndexMobile({ posts }: Props) {
  const pathname = usePathname();
  const { markRouteReady } = useContext(TransitionContext);
  const { collapsingKey, navigate } = useCollapseNavigate(COLLAPSE_DURATION_MS);
  const isCollapsing = collapsingKey !== null;

  useEffect(() => {
    markRouteReady(pathname);
  }, [pathname, markRouteReady]);

  return (
    <div className={styles.blogIndexMobile}>
      <p className={styles.blogMobileBreadcrumb}>
        <Link href='/'>{'⌂ HOME'}</Link>
        {' -> '}
        <Link href='/blog'>BLOG [UNSTABLE FINDINGS]</Link>
      </p>
      <section className={styles.blogIndexMobileHero}>
        <h1 className={styles.blogIndexMobileHeroTitle}>
          <img
            src='/assets/image/mobile/LAB_NOTES.svg'
            alt=''
            aria-hidden='true'
          />
          LAB NOTES
        </h1>
        <p className={styles.blogIndexMobileHeroIntro}>
          NOTES FROM THE LAB.
          <br />
          HALF EXPERIMENTS, HALF ACCIDENTS.
          <br />
          SOME IDEAS ESCAPED CONTAINMENT.
        </p>
      </section>
      <div className={styles.blogIndexMobileList}>
        {posts.map((post, index) => (
          <BlogCardMobile
            key={post.id}
            post={post}
            eager={index === 0}
            isCollapsing={isCollapsing}
            onNavigate={(href) => navigate(href, post.slug)}
          />
        ))}
      </div>
    </div>
  );
}
